import type { SignInCodeChannel } from '@chukua/common';
import { Alert, Button, Form } from 'antd';
import { Mail, MessageCircle, MessageSquare } from 'lucide-react';
import { CodeInput } from '../../../shared/components/CodeInput';
import { DisplayedCodeNotice } from '../../../shared/components/DisplayedCodeNotice';
import type { SentSignInCode, SignInCode } from '../hooks/use-sign-in-code';
import { formatCountdown } from '../policies/resend-countdown.policy';
import { codeStepLead, codeStepTitle, switchChannelLabel } from '../policies/sign-in-code-copy.policy';

const CHANNEL_ICONS: Record<SignInCodeChannel, typeof Mail> = { email: Mail, whatsapp: MessageCircle, sms: MessageSquare };

interface LoginSignInCodeFormProps {
  step: SignInCode;
  onUseDifferentIdentifier: () => void;
  onUsePassword: () => void;
}

function Lead({ sent }: { sent: SentSignInCode }) {
  const { before, target, after } = codeStepLead(sent.identifier, sent.channel);
  return (
    <p className="mt-1.5 mb-8 text-sm text-muted">
      {before}
      <strong className="font-semibold text-fg">{target}</strong>
      {after}
    </p>
  );
}

function ResendCode({ step, sent }: { step: SignInCode; sent: SentSignInCode }) {
  if (step.secondsLeft > 0) return <p className="py-1 text-muted">Send another code in {formatCountdown(step.secondsLeft)}</p>;
  return (
    <Button type="link" className="px-0" disabled={step.submitting} onClick={() => step.resendBy(sent.channel)}>
      Send another code
    </Button>
  );
}

/** The server refuses a second code within the minute, so the other channels wait out the countdown too. */
function OtherChannels({ step }: { step: SignInCode }) {
  return step.otherChannels.map((channel) => {
    const Icon = CHANNEL_ICONS[channel];
    return (
      <Button
        key={channel}
        type="link"
        className="px-0"
        icon={<Icon className="size-4" />}
        disabled={step.submitting || step.secondsLeft > 0}
        onClick={() => step.resendBy(channel)}
      >
        {switchChannelLabel(channel)}
      </Button>
    );
  });
}

export function LoginSignInCodeForm({ step, onUseDifferentIdentifier, onUsePassword }: LoginSignInCodeFormProps) {
  const { sent } = step;
  if (!sent) return null;

  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{codeStepTitle(sent.channel)}</h1>
      <Lead sent={sent} />
      {sent.displayedCode && <DisplayedCodeNotice className="mb-5" code={sent.displayedCode} />}
      {step.error && <Alert className="mb-5" type="error" title={step.error} showIcon />}
      <Form<{ code: string }> layout="vertical" size="large" requiredMark={false} onFinish={(values) => step.verify(values.code)} disabled={step.submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter the 6-digit code' }]}>
          <CodeInput autoFocus onComplete={(code) => void step.verify(code)} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" block loading={step.submitting}>
            Sign in
          </Button>
        </Form.Item>
      </Form>
      <div className="mt-6 flex flex-col items-start gap-1 text-sm">
        <p className="text-muted">Not there after a minute?</p>
        <ResendCode step={step} sent={sent} />
        <OtherChannels step={step} />
        <Button type="link" className="px-0" onClick={onUseDifferentIdentifier}>
          Use a different email or number
        </Button>
        <Button type="link" className="px-0" onClick={onUsePassword}>
          Sign in with a password
        </Button>
      </div>
    </>
  );
}
